import { PERIOD_YOY_GRANULARITY } from '../../../filters/lib/statisticsPeriodYoyHelpers';
import { TREND_AXIS_MODE } from '../../../filters/lib/statisticsTrendDimension';

/** 年月同比分类轴：竖向柱图须留边距防首尾裁切；折线按月可贴边 */
export const resolveYearMonthsCompareBoundaryGap = ({ isBar, horizontalBar, granularity }) =>
	isBar && !horizontalBar ? true : granularity === PERIOD_YOY_GRANULARITY.QUARTER;

/** 预览/趋势横轴：默认尽量露出 12 个类目，超出再滚动 */
export const CATEGORY_AXIS_VISIBLE_COUNT = 12;
const CUSTOMER_LABEL_CHARS_PER_LINE = 10;

/** 竖向柱状图：横轴（分类轴）组间距；仅第一条系列设置 barGap / barCategoryGap */
export const resolveVerticalCompareBarGapSeriesProps = (index = 0) =>
	index === 0 ? { barGap: '8%', barCategoryGap: '22%' } : null;

export const MIXED_COMPARE_BAR_X_AXIS_INDEX = 0;
export const MIXED_COMPARE_LINE_X_AXIS_INDEX = 1;
/** 柱+折混合：柱在下、折线在上，避免对比年折线点被基准柱遮挡 */
export const MIXED_COMPARE_BAR_Z = 2;
export const MIXED_COMPARE_LINE_Z = 4;
/** 柱+折竖向混合：柱层级低于折线；悬停/点击由 ECharts item 事件命中当前系列 */
export const resolveMixedCompareBarSeriesExtra = () => ({ z: MIXED_COMPARE_BAR_Z, triggerEvent: true });

/** 柱+折竖向混合：折线绑独立分类轴（配置与柱轴一致、仅隐藏），贴类目中心，不受分组柱槽位影响 */
export const buildMixedCompareCategoryXAxes = (categories = [], chartTheme = {}, { barAxisExtra = {} } = {}) => {
	const barAxis = {
		type: 'category',
		data: categories,
		boundaryGap: true,
		gridIndex: 0,
		...chartTheme.axis,
		axisTick: { alignWithLabel: true },
		splitLine: { show: false },
		...barAxisExtra,
	};

	return [
		barAxis,
		{
			...barAxis,
			show: false,
			axisPointer: { show: false },
			axisLine: { show: false },
			axisTick: { show: false },
			axisLabel: { show: false },
		},
	];
};

/** 横向柱状图底边留白，避免底部数值轴贴容器下缘 */
export const HORIZONTAL_COMPARE_BAR_GRID_BOTTOM = 12;
export const HORIZONTAL_COMPARE_BAR_DUAL_AXIS_GRID_BOTTOM = 32;

export const resolveHorizontalCompareBarGridBottom = (useDualAxis = false) =>
	useDualAxis ? HORIZONTAL_COMPARE_BAR_DUAL_AXIS_GRID_BOTTOM : HORIZONTAL_COMPARE_BAR_GRID_BOTTOM;

/** 类目数 > visibleCount 时启用滑块 + 内嵌拖动；默认露出 visibleCount 个 */
export const buildCategoryAxisDataZoom = ({
	count = 0,
	visibleCount = CATEGORY_AXIS_VISIBLE_COUNT,
	chartTheme = {},
	axisIndex = 0,
	categoryOnY = false,
	xAxisIndexList = null,
} = {}) => {
	if (count <= visibleCount) return [];
	const endPercent = Math.max(1, Math.min(100, Math.round((visibleCount / count) * 100)));
	const zoomAxisKey = categoryOnY ? 'yAxisIndex' : 'xAxisIndex';
	const axisTarget = Array.isArray(xAxisIndexList) && xAxisIndexList.length ? xAxisIndexList : axisIndex;
	const labelColor = chartTheme?.axis?.axisLabel?.color ?? '#94a3b8';

	return [
		{
			type: 'slider',
			show: true,
			[zoomAxisKey]: axisTarget,
			...(categoryOnY ? { width: 12, right: 6 } : { height: 14, bottom: 4 }),
			borderColor: 'transparent',
			backgroundColor: 'transparent',
			fillerColor: 'rgba(99, 102, 241, 0.12)',
			handleSize: 12,
			handleStyle: { color: chartTheme?.brandPrimary ?? '#6366f1' },
			textStyle: { color: labelColor, fontSize: 9 },
			brushSelect: false,
			start: 0,
			end: endPercent,
		},
		{
			type: 'inside',
			[zoomAxisKey]: axisTarget,
			start: 0,
			end: endPercent,
			zoomOnMouseWheel: false,
			moveOnMouseMove: true,
			moveOnMouseWheel: true,
		},
	];
};

/** 趋势对比 · 纵向柱状图分类轴：默认露出 12 个，超出横向滚动 */
export const buildPeriodCompareCategoryAxisLayout = (categories = [], chartTheme = {}, { anchorIndex = -1, accentColor = '' } = {}) => {
	const count = categories.length;
	const needsZoom = count > CATEGORY_AXIS_VISIBLE_COUNT;
	const rotate = !needsZoom && count > 8 ? 18 : 0;
	const labelLineCount = rotate > 0 ? 2 : anchorIndex >= 0 ? 2 : 1;
	const bottom = (needsZoom ? 28 : rotate > 0 ? 22 : anchorIndex >= 0 ? 16 : 8) + (labelLineCount - 1) * 10;
	const labelColor = chartTheme?.tooltip?.textStyle?.color ?? chartTheme?.axis?.axisLabel?.color ?? '#344050';
	const compact = needsZoom || rotate > 0;

	const axisLabel = {
		color: labelColor,
		fontSize: compact ? 9 : 11,
		fontWeight: 600,
		interval: 0,
		hideOverlap: needsZoom,
		showMinLabel: true,
		showMaxLabel: true,
		margin: 8,
		rotate,
		overflow: compact ? 'truncate' : 'none',
		...(compact ? { width: needsZoom ? 56 : 72 } : {}),
		formatter: (value, index) => {
			if (anchorIndex >= 0 && index === anchorIndex) return `{anchor|${value}}\n{tag|基准}`;
			return value;
		},
		...(anchorIndex >= 0
			? {
					rich: {
						anchor: { fontWeight: 700, fontSize: compact ? 9 : 11, color: accentColor || labelColor, lineHeight: 16 },
						tag: { fontSize: 9, color: accentColor || labelColor, lineHeight: 13 },
					},
				}
			: {}),
	};

	return {
		axisLabel,
		dataZoom: buildCategoryAxisDataZoom({ count, chartTheme, axisIndex: 0 }),
		gridBottom: bottom,
	};
};

/** 横向柱状图：按系列数调节柱宽；barCategoryGap 控制分类组间距 */
export const resolveHorizontalCompareBarSeriesStyle = (seriesCount = 1) => {
	const count = Math.max(1, Number(seriesCount) || 1);
	if (count >= 4) {
		return { barMaxWidth: 14, barCategoryGap: '38%', barGap: '6%' };
	}
	if (count >= 2) {
		return { barMaxWidth: 18, barCategoryGap: '42%', barGap: '8%' };
	}
	return { barMaxWidth: 24, barCategoryGap: '48%', barGap: '10%' };
};

/** 横向柱：每个 value 轴的第一条系列设置 barGap / barCategoryGap */
export const resolveHorizontalBarGapSeriesProps = (seriesDefs = [], index = 0, barStyle = null) => {
	if (!barStyle || !Array.isArray(seriesDefs) || index < 0 || index >= seriesDefs.length) return null;
	const axisIndex = seriesDefs[index]?.yAxisIndex ?? 0;
	const isFirstOnAxis = seriesDefs.findIndex((item) => (item?.yAxisIndex ?? 0) === axisIndex) === index;
	return isFirstOnAxis ? { barGap: barStyle.barGap, barCategoryGap: barStyle.barCategoryGap } : null;
};

const wrapFullCategoryAxisLabel = (value, charsPerLine = CUSTOMER_LABEL_CHARS_PER_LINE) => {
	const text = String(value ?? '').trim();
	if (!text || text.length <= charsPerLine) return text;
	const lines = [];
	for (let i = 0; i < text.length; i += charsPerLine) {
		lines.push(text.slice(i, i + charsPerLine));
	}
	return lines.join('\n');
};

const resolveCustomerAxisLabelLineCount = (axisLabels = [], charsPerLine = CUSTOMER_LABEL_CHARS_PER_LINE) => {
	if (!Array.isArray(axisLabels) || !axisLabels.length) return 1;
	let maxLines = 1;
	for (const label of axisLabels) {
		const lineCount = Math.ceil(String(label ?? '').trim().length / charsPerLine) || 1;
		if (lineCount > maxLines) maxLines = lineCount;
	}
	return maxLines;
};

export const resolveDimensionTrendChartHeight = (dimensionAxisMode, fallback = '9.5rem', axisLabels = []) => {
	if (dimensionAxisMode !== TREND_AXIS_MODE.CUSTOMER) return fallback;
	const labelLineCount = resolveCustomerAxisLabelLineCount(axisLabels);
	if (labelLineCount >= 3) return '16rem';
	if (labelLineCount >= 2) return '14.5rem';
	return '13rem';
};

export const buildDimensionCategoryAxisLayout = (axisLabels = [], dimensionAxisMode = '', chartTheme = {}, { categoryOnY = false } = {}) => {
	const count = axisLabels.length;
	const isCustomer = dimensionAxisMode === TREND_AXIS_MODE.CUSTOMER;
	const needsZoom = count > CATEGORY_AXIS_VISIBLE_COUNT;
	const axisZoomIndex = 0;

	if (!isCustomer) {
		const rotate = !categoryOnY && !needsZoom && count > 8 ? 24 : 0;
		return {
			gridBottom: categoryOnY ? HORIZONTAL_COMPARE_BAR_GRID_BOTTOM : needsZoom ? 28 : rotate > 0 ? 28 : 4,
			gridLeft: categoryOnY ? (needsZoom ? 72 : count > 4 ? 72 : 8) : 8,
			axisLabel: {
				fontSize: 9,
				interval: 0,
				rotate: categoryOnY ? 0 : rotate,
			},
			dataZoom: buildCategoryAxisDataZoom({ count, chartTheme, axisIndex: axisZoomIndex, categoryOnY }),
			categoryOnY,
		};
	}

	const labelLineCount = resolveCustomerAxisLabelLineCount(axisLabels);
	const labelAreaHeight = labelLineCount * 13 + 10;
	const labelAreaWidth = Math.min(128, labelLineCount * 54 + 40);
	const sliderAreaHeight = needsZoom && !categoryOnY ? 24 : 0;
	const sliderAreaWidth = needsZoom && categoryOnY ? 18 : 0;
	const sharedAxisLabel = {
		fontSize: 9,
		interval: 0,
		margin: 8,
		lineHeight: 13,
		hideOverlap: false,
		showMinLabel: true,
		showMaxLabel: true,
		overflow: 'none',
		formatter: (value) => wrapFullCategoryAxisLabel(value),
	};

	return {
		gridBottom: categoryOnY ? 4 : labelAreaHeight + sliderAreaHeight,
		gridLeft: categoryOnY ? labelAreaWidth + sliderAreaWidth : 8,
		axisLabel: categoryOnY ? sharedAxisLabel : { ...sharedAxisLabel, rotate: 0 },
		dataZoom: buildCategoryAxisDataZoom({ count, chartTheme, axisIndex: axisZoomIndex, categoryOnY }),
		categoryOnY,
	};
};
